import { useMemo, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { projectReportChart } from '../../../reportExplorer/reportChartModel';
import { ReportMetric } from '../../../reportExplorer/reportEnums';
import { performanceEntityOptions, projectEntityPerformanceChart } from '../../chartModel';
import { PerformanceDrilldownType, type PerformanceAnalyzerRange } from '../../model';
import {
  buildCounterpartRequest,
  buildPerformanceKpiRequest,
  buildPublisherDemandDetailRequest,
  buildTopEntityRequest,
  performanceKpiMetrics,
} from '../../reportRequests';
import { PerformanceQueryKind, usePerformanceQuery } from '../../hooks';
import { EntityControl, MetricControl } from '../PerformanceControls';
import { PerformanceChartCard } from '../PerformanceChartCard';

export function PublisherDemandPerformance({ range }: { range: PerformanceAnalyzerRange }) {
  const comparisonEnabled = Boolean(range.comparison);
  const [drilldown, setDrilldown] = useState(PerformanceDrilldownType.Publishers);
  const [metrics, setMetrics] = useState<ReportMetric[]>([ReportMetric.Revenue]);
  const [selectedEntity, setSelectedEntity] = useState('');
  const [selectedCounterpart, setSelectedCounterpart] = useState('');

  const kpiRequest = buildPerformanceKpiRequest(range.primary);
  const kpiComparisonRequest = buildPerformanceKpiRequest(range.comparison ?? range.primary);
  const topRequest = buildTopEntityRequest(range.primary, drilldown, metrics);
  const topComparisonRequest = buildTopEntityRequest(
    range.comparison ?? range.primary,
    drilldown,
    metrics,
  );
  const counterpartRequest = buildCounterpartRequest(
    range.primary,
    drilldown,
    selectedEntity,
    metrics,
  );
  const counterpartComparisonRequest = buildCounterpartRequest(
    range.comparison ?? range.primary,
    drilldown,
    selectedEntity,
    metrics,
  );
  const detailRequest = buildPublisherDemandDetailRequest(
    range.primary,
    drilldown,
    selectedEntity,
    selectedCounterpart,
  );
  const detailComparisonRequest = buildPublisherDemandDetailRequest(
    range.comparison ?? range.primary,
    drilldown,
    selectedEntity,
    selectedCounterpart,
  );

  const kpi = usePerformanceQuery(PerformanceQueryKind.Standard, kpiRequest);
  const kpiComparison = usePerformanceQuery(
    PerformanceQueryKind.Standard,
    kpiComparisonRequest,
    Boolean(range.comparison),
  );
  const top = usePerformanceQuery(PerformanceQueryKind.Standard, topRequest, metrics.length > 0);
  const topComparison = usePerformanceQuery(
    PerformanceQueryKind.Standard,
    topComparisonRequest,
    Boolean(range.comparison) && metrics.length > 0,
  );
  const counterpart = usePerformanceQuery(
    PerformanceQueryKind.Standard,
    counterpartRequest,
    Boolean(selectedEntity),
  );
  const counterpartComparison = usePerformanceQuery(
    PerformanceQueryKind.Standard,
    counterpartComparisonRequest,
    Boolean(range.comparison) && Boolean(selectedEntity),
  );
  const detail = usePerformanceQuery(
    PerformanceQueryKind.Standard,
    detailRequest,
    Boolean(selectedCounterpart),
  );
  const detailComparison = usePerformanceQuery(
    PerformanceQueryKind.Standard,
    detailComparisonRequest,
    Boolean(range.comparison) && Boolean(selectedCounterpart),
  );

  const topIdentity =
    drilldown === PerformanceDrilldownType.Publishers
      ? { id: 'publisherId', label: 'publisherName' }
      : { id: 'bidderCode', label: 'bidderCode' };
  const counterpartIdentity =
    drilldown === PerformanceDrilldownType.Publishers
      ? { id: 'bidderCode', label: 'bidderCode' }
      : { id: 'publisherId', label: 'publisherName' };
  const topEntities = useMemo(
    () => performanceEntityOptions(top.data, topIdentity.id, topIdentity.label),
    [top.data, topIdentity.id, topIdentity.label],
  );
  const counterpartEntities = useMemo(
    () =>
      performanceEntityOptions(counterpart.data, counterpartIdentity.id, counterpartIdentity.label),
    [counterpart.data, counterpartIdentity.id, counterpartIdentity.label],
  );
  const kpiProjection = projectReportChart(
    kpiRequest,
    kpi.data,
    range.comparison ? { response: kpiComparison.data, days: 1 } : undefined,
  );
  const topProjection = projectEntityPerformanceChart(
    top.data,
    range.comparison ? topComparison.data : undefined,
    topEntities,
    {
      identityField: topIdentity.id,
      metrics,
    },
  );
  const counterpartProjection = projectEntityPerformanceChart(
    counterpart.data,
    range.comparison ? counterpartComparison.data : undefined,
    counterpartEntities,
    {
      identityField: counterpartIdentity.id,
      metrics,
    },
  );
  const detailProjection = projectReportChart(
    detailRequest,
    detail.data,
    range.comparison ? { response: detailComparison.data, days: 1 } : undefined,
  );

  function resetDrilldown(type: PerformanceDrilldownType) {
    setDrilldown(type);
    setSelectedEntity('');
    setSelectedCounterpart('');
  }

  return (
    <div className="space-y-4">
      <PerformanceChartCard
        title={dashboardT('performanceAnalyzer.kpiTitle')}
        {...kpiProjection}
        loading={kpi.isLoading || (comparisonEnabled && kpiComparison.isLoading)}
        error={kpi.isError || (comparisonEnabled && kpiComparison.isError)}
        controls={<DrilldownControl value={drilldown} onChange={resetDrilldown} />}
      />
      <PerformanceChartCard
        title={dashboardT(
          drilldown === PerformanceDrilldownType.Publishers
            ? 'performanceAnalyzer.topPublishers'
            : 'performanceAnalyzer.topPartners',
        )}
        {...topProjection}
        loading={top.isLoading || (comparisonEnabled && topComparison.isLoading)}
        error={top.isError || (comparisonEnabled && topComparison.isError)}
        controls={
          <MetricControl
            metrics={performanceKpiMetrics}
            selected={metrics}
            onChange={(value) => {
              setMetrics(value);
              setSelectedEntity('');
              setSelectedCounterpart('');
            }}
          />
        }
        action={
          <EntityControl
            label={dashboardT(
              drilldown === PerformanceDrilldownType.Publishers
                ? 'performanceAnalyzer.selectPublisher'
                : 'performanceAnalyzer.selectPartner',
            )}
            options={topEntities}
            value={selectedEntity}
            onChange={(value) => {
              setSelectedEntity(value);
              setSelectedCounterpart('');
            }}
          />
        }
      />
      {selectedEntity ? (
        <PerformanceChartCard
          title={dashboardT(
            drilldown === PerformanceDrilldownType.Publishers
              ? 'performanceAnalyzer.publisherPartners'
              : 'performanceAnalyzer.partnerPublishers',
          )}
          {...counterpartProjection}
          loading={counterpart.isLoading || (comparisonEnabled && counterpartComparison.isLoading)}
          error={counterpart.isError || (comparisonEnabled && counterpartComparison.isError)}
          action={
            <EntityControl
              label={dashboardT(
                drilldown === PerformanceDrilldownType.Publishers
                  ? 'performanceAnalyzer.selectPartner'
                  : 'performanceAnalyzer.selectPublisher',
              )}
              options={counterpartEntities}
              value={selectedCounterpart}
              onChange={setSelectedCounterpart}
            />
          }
        />
      ) : null}
      {selectedCounterpart ? (
        <PerformanceChartCard
          title={dashboardT('performanceAnalyzer.details')}
          {...detailProjection}
          loading={detail.isLoading || (comparisonEnabled && detailComparison.isLoading)}
          error={detail.isError || (comparisonEnabled && detailComparison.isError)}
        />
      ) : null}
    </div>
  );
}

function DrilldownControl({
  value,
  onChange,
}: {
  value: PerformanceDrilldownType;
  onChange: (value: PerformanceDrilldownType) => void;
}) {
  return (
    <Select
      value={value}
      onValueChange={(next) => {
        onChange(next as PerformanceDrilldownType);
      }}
    >
      <SelectTrigger
        className="w-full sm:w-64"
        aria-label={dashboardT('performanceAnalyzer.selectDrilldown')}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={PerformanceDrilldownType.Publishers}>
          {dashboardT('performanceAnalyzer.publishers')}
        </SelectItem>
        <SelectItem value={PerformanceDrilldownType.DemandPartners}>
          {dashboardT('performanceAnalyzer.demandPartners')}
        </SelectItem>
      </SelectContent>
    </Select>
  );
}
